import React, { memo } from 'react';
import { cn } from '@/lib/utils';
import { 
  Globe, 
  MessageSquare, 
  Instagram, 
  Facebook,
  Filter,
  LayoutGrid
} from 'lucide-react';
import { FaWhatsapp, FaFacebookMessenger } from 'react-icons/fa';

/**
 * Channel Filter Component
 * Inline horizontal filter pills for filtering dashboard data by channel
 * 
 * Usage:
 * <ChannelFilter
 *   selectedChannel={selectedChannel}
 *   onChannelChange={setSelectedChannel}
 *   channels={channels}
 *   isChannelConnected={isChannelConnected}
 * />
 */

// Default channel definitions
const DEFAULT_CHANNELS = [
  { type: 'all', name: 'Semua', icon: LayoutGrid, color: 'blue' },
  { type: 'whatsapp', name: 'WhatsApp', icon: FaWhatsapp, color: 'green' },
  { type: 'messenger', name: 'Messenger', icon: FaFacebookMessenger, color: 'blue' },
  { type: 'instagram', name: 'Instagram', icon: Instagram, color: 'pink' },
  { type: 'website', name: 'Website', icon: Globe, color: 'amber' }
];

// Color mappings for active states
const CHANNEL_COLORS = {
  all: {
    active: 'bg-indigo-600 text-white border-indigo-600',
    hover: 'hover:bg-indigo-50 hover:border-indigo-300 dark:hover:bg-indigo-950/30'
  },
  whatsapp: {
    active: 'bg-green-600 text-white border-green-600',
    hover: 'hover:bg-green-50 hover:border-green-300 dark:hover:bg-green-900/20'
  },
  messenger: {
    active: 'bg-indigo-600 text-white border-indigo-600',
    hover: 'hover:bg-indigo-50 hover:border-indigo-300 dark:hover:bg-indigo-950/30'
  },
  instagram: {
    active: 'bg-gradient-to-r from-purple-600 to-pink-600 text-white border-pink-600',
    hover: 'hover:bg-pink-50 hover:border-pink-300 dark:hover:bg-pink-900/20'
  },
  website: {
    active: 'bg-amber-600 text-white border-amber-600',
    hover: 'hover:bg-amber-50 hover:border-amber-300 dark:hover:bg-amber-900/20'
  }
};

const ChannelFilterItem = memo(({ 
  channel, 
  isActive, 
  isConnected = true,
  onClick,
  showLabel = true,
  size = 'default'
}) => {
  const Icon = channel.icon;
  const colors = CHANNEL_COLORS[channel.type] || CHANNEL_COLORS.all;
  
  const sizeClasses = {
    small: 'h-8 px-2.5 text-xs gap-1.5',
    default: 'h-9 px-3 text-xs gap-2',
    large: 'h-10 px-4 text-sm gap-2'
  };

  // Icon-only size classes (for mobile)
  const iconOnlySizeClasses = {
    small: 'h-8 w-8',
    default: 'h-9 w-9',
    large: 'h-10 w-10'
  };

  const iconSizes = {
    small: 'w-4 h-4',
    default: 'w-4 h-4',
    large: 'w-5 h-5'
  };

  // Don't show disconnected channels (except 'all')
  if (channel.type !== 'all' && !isConnected) {
    return null;
  }

  return (
    <button
      onClick={onClick}
      data-testid={`channel-filter-${channel.type}`}
      title={channel.name}
      className={cn(
        'inline-flex items-center justify-center rounded-full font-medium transition-all duration-200',
        'border whitespace-nowrap flex-shrink-0',
        showLabel ? sizeClasses[size] : iconOnlySizeClasses[size],
        isActive
          ? colors.active
          : cn(
              'bg-white dark:bg-zinc-900 text-slate-600 dark:text-zinc-400',
              'border-slate-200 dark:border-zinc-800',
              colors.hover
            )
      )}
    >
      <Icon className={cn(iconSizes[size], isActive ? '' : 'opacity-70')} />
      {showLabel && <span className="hidden sm:inline">{channel.name}</span>}
    </button>
  );
});

ChannelFilterItem.displayName = 'ChannelFilterItem';

export const ChannelFilter = memo(({
  selectedChannel = 'all',
  onChannelChange,
  channels = DEFAULT_CHANNELS,
  connectedChannels = [],
  isChannelConnected,
  showLabels = true,
  size = 'default',
  className = ''
}) => {
  
  // Helper to check if channel is connected
  const checkConnected = (channelType) => {
    if (channelType === 'all') return true;
    if (isChannelConnected) return isChannelConnected(channelType);
    return connectedChannels.includes(channelType);
  };

  // Filter to only show connected channels + 'all'
  const visibleChannels = channels.filter(ch => 
    ch.type === 'all' || checkConnected(ch.type)
  );

  // Always render at least "all" option - don't hide filter
  // This ensures the filter UI is visible for consistency

  return (
    <div 
      className={cn(
        'flex items-center gap-1.5',
        className
      )}
    >
      {visibleChannels.map((channel) => (
        <ChannelFilterItem
          key={channel.type}
          channel={channel}
          isActive={selectedChannel === channel.type}
          isConnected={checkConnected(channel.type)}
          onClick={() => onChannelChange?.(channel.type)}
          showLabel={showLabels}
          size={size}
        />
      ))}
    </div>
  );
});

ChannelFilter.displayName = 'ChannelFilter';

/**
 * Compact Channel Filter - Icon only version for tight spaces
 */
export const ChannelFilterCompact = memo((props) => (
  <ChannelFilter {...props} showLabels={false} size="small" />
));

ChannelFilterCompact.displayName = 'ChannelFilterCompact';

export default ChannelFilter;
